import { MessageCircle, Phone, Mail, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CONTACT, waLink, WA_DEFAULT_MSG } from "@/lib/site";

const CHANNELS = [
  {
    label: "WhatsApp",
    value: CONTACT.whatsappDisplay,
    icon: MessageCircle,
    href: waLink(WA_DEFAULT_MSG),
    external: true,
    testid: "contact-card-whatsapp",
    accent: "text-[#25D366]",
    tile: "bg-[#25D366]/10",
  },
  {
    label: "Call Dispatch",
    value: CONTACT.callDisplay,
    icon: Phone,
    href: `tel:${CONTACT.callNumber}`,
    external: false,
    testid: "contact-card-call",
    accent: "text-[#FF6B00]",
    tile: "bg-[#FF6B00]/10",
  },
  {
    label: "Email",
    value: CONTACT.email,
    icon: Mail,
    href: `mailto:${CONTACT.email}`,
    external: false,
    testid: "contact-card-email",
    accent: "text-white",
    tile: "bg-white/10",
  },
];

export const CtaSection = () => (
  <section
    id="enquiry"
    className="scroll-mt-24 border-t border-white/10 bg-[#0D1424] py-20 md:py-28"
    data-testid="enquiry-section"
  >
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#FF6B00]">
              <span className="inline-block h-px w-8 bg-[#FF6B00]" aria-hidden="true" />
              Contract Partnership
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
              Let&apos;s talk{" "}
              <span className="text-stroke">contract</span>{" "}
              <span className="text-[#FF6B00]">capacity.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300">
              Tell us about your routes, volumes and vehicle needs. We&apos;ll come
              back with a dedicated proposal for your operation — from a single
              contracted bakkie to a multi-vehicle overflow arrangement.
            </p>
          </Reveal>

          <div className="mt-10 space-y-3">
            {CHANNELS.map((c, i) => (
              <Reveal key={c.label} delay={0.1 + i * 0.08}>
                <a
                  href={c.href}
                  target={c.external ? "_blank" : undefined}
                  rel={c.external ? "noopener noreferrer" : undefined}
                  data-testid={c.testid}
                  className="group flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#080C14] p-5 transition-all duration-300 hover:border-[#FF6B00]/50"
                >
                  <span className="flex items-center gap-4">
                    <span
                      className={`flex h-11 w-11 items-center justify-center rounded-lg ${c.tile} ${c.accent}`}
                    >
                      <c.icon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                        {c.label}
                      </span>
                      <span className="mt-0.5 block font-display text-base font-bold tracking-tight text-white md:text-lg">
                        {c.value}
                      </span>
                    </span>
                  </span>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-600 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#FF6B00]" />
                </a>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur md:p-8">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-white">
              Partnership Enquiry
            </p>
            <p className="mt-1.5 text-sm text-slate-400">
              Fields marked required help us route your brief to the right
              decision-maker.
            </p>
            <div className="mt-6">
              <EnquiryForm />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
